import { useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useClinicBasePath } from '@/hooks/useClinicBasePath';
import { useQuery } from '@tanstack/react-query';
import {
  Page,
  Card,
  DataTable,
  Badge,
  Button,
  Banner,
  InlineGrid,
  Text,
  SkeletonBodyText,
  EmptyState,
  Box,
  InlineStack,
  BlockStack,
  TextField,
} from '@shopify/polaris';
import { clinicPortalApi } from '@/services';
import { useClinicAuth } from '../../contexts/ClinicAuthContext';
import { orderNumber } from '@/utils/orderNumber';

export default function ClinicDashboard() {
  const navigate = useNavigate();
  const base = useClinicBasePath();
  const { user } = useClinicAuth();
  const [copied, setCopied] = useState(false);

  const patientFormUrl = `https://app.healfaster.ca/clinic-store/${user?.clinicId || ''}`;

  const handleCopyLink = useCallback(() => {
    navigator.clipboard.writeText(patientFormUrl).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  }, [patientFormUrl]);

  const { data, isLoading, error } = useQuery({
    queryKey: ['clinic-dashboard'],
    queryFn: () => clinicPortalApi.getDashboard(),
  });

  const { data: pendingRequests = [] } = useQuery({
    queryKey: ['clinic-pending-requests'],
    queryFn: () => clinicPortalApi.getPendingRequests(),
  });

  const rows = (data?.recentOrders || []).map((order) => [
    orderNumber(order),
    order.patientName || 'N/A',
    <Badge key={order.id} tone="info">{order.status}</Badge>,
    new Date(order.createdAt).toLocaleDateString(),
  ]);

  if (error) {
    return (
      <Page title="Clinic Dashboard">
        <Card>
          <EmptyState
            heading="Error loading dashboard"
            image="https://cdn.shopify.com/s/files/1/0262/4071/2726/files/emptystate-files.png"
          >
            <p>Please try again later.</p>
          </EmptyState>
        </Card>
      </Page>
    );
  }

  return (
    <Page title="Clinic Dashboard">
      <BlockStack gap="500">
        {pendingRequests.length > 0 && (
          <Banner tone="warning" title={`${pendingRequests.length} patient request${pendingRequests.length === 1 ? '' : 's'} awaiting your approval`}>
            <BlockStack gap="200">
              {pendingRequests.slice(0, 5).map((req) => (
                <InlineStack key={req.id} align="space-between" blockAlign="center">
                  <Text as="p">
                    <strong>{req.rental.patientName || 'Patient'}</strong> requested a{' '}
                    {req.weeksRequested}-week extension on order{' '}
                    {orderNumber({ ...req.rental, id: req.rentalId })}
                  </Text>
                  <Button
                    onClick={() => navigate(`${base}/orders/${req.rentalId}`)}
                  >
                    Review
                  </Button>
                </InlineStack>
              ))}
              {pendingRequests.length > 5 && (
                <Text as="p" tone="subdued">
                  +{pendingRequests.length - 5} more
                </Text>
              )}
            </BlockStack>
          </Banner>
        )}

        <Card>
          <BlockStack gap="300">
            <Text as="h2" variant="headingMd">Patient Form Link</Text>
            <Text as="p" tone="subdued" variant="bodySm">
              Share this link with patients to submit their rental request form.
            </Text>
            <InlineStack gap="200" blockAlign="end">
              <div style={{ flex: 1 }}>
                <TextField
                  label=""
                  labelHidden
                  value={patientFormUrl}
                  readOnly
                  autoComplete="off"
                />
              </div>
              <Button onClick={handleCopyLink}>
                {copied ? 'Copied!' : 'Copy'}
              </Button>
            </InlineStack>
          </BlockStack>
        </Card>

        {isLoading ? (
          <Card>
            <SkeletonBodyText lines={4} />
          </Card>
        ) : (
          <InlineGrid columns={2} gap="400">
            <Card>
              <BlockStack gap="200">
                <Text as="p" tone="subdued" variant="bodyMd">Active Rentals</Text>
                <Text as="p" variant="heading2xl">{data?.activeRentals ?? 0}</Text>
              </BlockStack>
            </Card>
            <Card>
              <BlockStack gap="200">
                <Text as="p" tone="subdued" variant="bodyMd">Pending Returns</Text>
                <Text as="p" variant="heading2xl">{data?.pendingReturns ?? 0}</Text>
              </BlockStack>
            </Card>
          </InlineGrid>
        )}

        <Card>
          <BlockStack gap="400">
            <InlineStack align="space-between">
              <Text as="h2" variant="headingMd">Recent Orders</Text>
              <Button onClick={() => navigate(`${base}/orders`)}>View all</Button>
            </InlineStack>

            {isLoading ? (
              <SkeletonBodyText lines={5} />
            ) : rows.length === 0 ? (
              <Box padding="400">
                <EmptyState
                  heading="No orders yet"
                  image="https://cdn.shopify.com/s/files/1/0262/4071/2726/files/emptystate-files.png"
                >
                  <p>Orders will appear here once placed.</p>
                </EmptyState>
              </Box>
            ) : (
              <DataTable
                columnContentTypes={['text', 'text', 'text', 'text']}
                headings={['Order #', 'Patient', 'Status', 'Date']}
                rows={rows}
              />
            )}
          </BlockStack>
        </Card>

        <Card>
          <BlockStack gap="200">
            <Text as="h2" variant="headingMd">Need to return equipment?</Text>
            <Text as="p" tone="subdued" variant="bodyMd">
              Submit a return request and our team will arrange pickup.
            </Text>
            <Box>
              <Button variant="primary" onClick={() => navigate(`${base}/return-request`)}>
                Request Return
              </Button>
            </Box>
          </BlockStack>
        </Card>
      </BlockStack>
    </Page>
  );
}
